import { useEffect, useState } from "react";
import {
  Bookmark,
  BookmarkPlus,
  X,
  Sparkles,
  Users as UsersIcon,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Built-in presets (always shown, not deletable). Designed around the most
// common recruiter workflows that compose the existing filters in useful ways.
const BUILTIN_PRESETS = [
  {
    id: "builtin_triage",
    name: "My triage queue",
    description: "Awaiting Response · Online App · most recently opened",
    filter: {
      filterStatus: "awaiting_response",
      filterSource: "online_application",
      sortBy: "opened_desc",
      search: "",
    },
  },
  {
    id: "builtin_hot_leads",
    name: "Hot leads",
    description: "New applicants · highest score first",
    filter: {
      filterStatus: "new",
      filterSource: "all",
      sortBy: "score_desc",
      search: "",
    },
  },
  {
    id: "builtin_stuck",
    name: "Stuck candidates",
    description: "Awaiting Response · oldest first",
    filter: {
      filterStatus: "awaiting_response",
      filterSource: "all",
      sortBy: "received_asc",
      search: "",
    },
  },
];

const filtersMatch = (a, b) =>
  a.filterStatus === b.filterStatus &&
  a.filterSource === b.filterSource &&
  a.sortBy === b.sortBy &&
  (a.search || "") === (b.search || "");

const isDefaultView = (f) =>
  f.filterStatus === "all" &&
  f.filterSource === "all" &&
  f.sortBy === "received_desc" &&
  !f.search;

const LEGACY_LS_KEY = "hireloop_applicants_presets";

export default function FilterPresets({
  filterStatus,
  filterSource,
  sortBy,
  search,
  onApply,
}) {
  const { user } = useAuth();
  const isTeamOwner = !!user && (user.team_owner_id || user.id) === user.id;
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftShared, setDraftShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/applicants/presets");
      setPresets(data.presets || []);
    } catch (e) {
      // Don't toast — the toolbar still works without presets.
      console.warn("Failed to load filter presets:", e);
    } finally {
      setLoading(false);
    }
  };

  // Initial load + one-time migration of any legacy localStorage presets.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (cancelled) return;
      try {
        const raw = localStorage.getItem(LEGACY_LS_KEY);
        if (!raw) return;
        const legacy = JSON.parse(raw);
        if (!Array.isArray(legacy) || !legacy.length) {
          localStorage.removeItem(LEGACY_LS_KEY);
          return;
        }
        let migrated = 0;
        for (const p of legacy) {
          try {
            await api.post("/applicants/presets", {
              name: p.name,
              filter: p.filter || {},
              is_team_shared: false,
            });
            migrated += 1;
          } catch {
            /* skip duplicates / invalid rows */
          }
        }
        localStorage.removeItem(LEGACY_LS_KEY);
        if (migrated > 0) {
          toast.success(
            `Migrated ${migrated} preset${migrated === 1 ? "" : "s"} from this browser`
          );
          await refresh();
        }
      } catch {
        /* ignore migration errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentFilter = { filterStatus, filterSource, sortBy, search };
  const activePresetId = (() => {
    for (const p of BUILTIN_PRESETS) {
      if (filtersMatch(p.filter, currentFilter)) return p.id;
    }
    for (const p of presets) {
      if (filtersMatch(p.filter, currentFilter)) return p.id;
    }
    return null;
  })();

  const personalPresets = presets.filter(
    (p) => !p.is_team_shared && p.created_by === user?.id
  );
  const teamPresets = presets.filter((p) => p.is_team_shared);

  const handleSave = async () => {
    const name = draftName.trim().slice(0, 40);
    if (!name) {
      toast.error("Give your preset a name");
      return;
    }
    if (
      [...BUILTIN_PRESETS, ...presets].some(
        (p) => p.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      toast.error("A preset with this name already exists");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/applicants/presets", {
        name,
        filter: currentFilter,
        is_team_shared: draftShared && isTeamOwner,
      });
      setPresets((prev) => [...prev, data]);
      setDraftName("");
      setDraftShared(false);
      setNaming(false);
      toast.success(
        data.is_team_shared
          ? `Saved & shared "${data.name}" with the team`
          : `Saved preset "${data.name}"`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const target = presets.find((p) => p.id === id);
    if (!target) return;
    if (
      target.is_team_shared &&
      !window.confirm(
        `"${target.name}" is shared with your team. Removing it will hide it for every teammate. Continue?`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/applicants/presets/${id}`);
      setPresets((prev) => prev.filter((p) => p.id !== id));
      toast.success("Preset removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete preset");
    }
  };

  const handleResetCounter = async (preset) => {
    if (
      !window.confirm(
        `Reset the usage counter for "${preset.name}"? Currently ${preset.apply_count}× — this can't be undone.`
      )
    ) {
      return;
    }
    try {
      const { data } = await api.post(
        `/applicants/presets/${preset.id}/reset-counter`
      );
      setPresets((prev) =>
        prev.map((p) => (p.id === data.id ? { ...p, ...data } : p))
      );
      toast.success(`Reset "${preset.name}" usage counter`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset counter");
    }
  };

  const handleApply = async (preset) => {
    onApply(preset.filter);
    // Fire-and-forget usage tracking for backend-backed presets only.
    if (!preset.id || preset.id.startsWith("builtin_")) return;
    try {
      const { data } = await api.post(`/applicants/presets/${preset.id}/apply`);
      setPresets((prev) =>
        prev.map((p) => (p.id === data.id ? { ...p, ...data } : p))
      );
    } catch {
      /* silent — counter is best-effort */
    }
  };

  const canResetPreset = (p) => {
    if (!p || !p.id || (p.apply_count || 0) <= 0) return false;
    if (p.created_by === user?.id) return true;
    if (p.is_team_shared && isTeamOwner) return true;
    return false;
  };

  const renderUsageBadge = (p) => {
    const n = p.apply_count || 0;
    if (n <= 0) return null;
    return (
      <>
        <span
          data-testid={`preset-${p.id}-usage`}
          title={
            p.last_applied_at
              ? `Last applied ${new Date(p.last_applied_at).toLocaleString()}`
              : ""
          }
          className="ml-1 inline-flex items-center rounded-full bg-slate-200/70 px-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-700"
        >
          {n}×
        </span>
        {canResetPreset(p) && (
          <button
            type="button"
            data-testid={`preset-${p.id}-reset-counter`}
            title="Reset usage counter"
            onClick={() => handleResetCounter(p)}
            className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        )}
      </>
    );
  };

  const renderPersonal = (p) => {
    const isActive = activePresetId === p.id;
    return (
      <span
        key={p.id}
        data-testid={`preset-${p.id}`}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
          isActive
            ? "border-blue-300 bg-blue-50 text-blue-700 shadow-sm"
            : "border-slate-200 bg-white text-slate-700"
        }`}
      >
        <button
          type="button"
          data-testid={`preset-${p.id}-apply`}
          title="Apply preset"
          onClick={() => handleApply(p)}
          className="inline-flex items-center gap-1 hover:text-blue-700"
        >
          <Bookmark className="h-3 w-3" />
          {p.name}
        </button>
        {renderUsageBadge(p)}
        <button
          type="button"
          data-testid={`preset-${p.id}-delete`}
          title={`Delete "${p.name}"`}
          onClick={() => handleDelete(p.id)}
          className="ml-1 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  };

  const renderTeam = (p) => {
    const isActive = activePresetId === p.id;
    const canDelete = p.created_by === user?.id || isTeamOwner;
    const byName = p.created_by_name || "teammate";
    return (
      <span
        key={p.id}
        data-testid={`preset-${p.id}`}
        title={`Shared with the team by ${byName}`}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
          isActive
            ? "border-violet-300 bg-violet-50 text-violet-700 shadow-sm"
            : "border-violet-200 bg-violet-50/50 text-violet-700"
        }`}
      >
        <button
          type="button"
          data-testid={`preset-${p.id}-apply`}
          onClick={() => handleApply(p)}
          className="inline-flex items-center gap-1 hover:text-violet-900"
        >
          <UsersIcon className="h-3 w-3" />
          {p.name}
        </button>
        {renderUsageBadge(p)}
        {canDelete && (
          <button
            type="button"
            data-testid={`preset-${p.id}-delete`}
            title={`Delete team preset "${p.name}"`}
            onClick={() => handleDelete(p.id)}
            className="ml-1 rounded-full p-0.5 text-violet-400 hover:bg-violet-100 hover:text-rose-600"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  };

  return (
    <div
      data-testid="applicants-filter-presets"
      className="mt-6 flex flex-wrap items-center gap-2"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        <Bookmark className="mr-1 inline h-3 w-3" />
        Presets
      </span>
      {BUILTIN_PRESETS.map((p) => {
        const isActive = activePresetId === p.id;
        return (
          <button
            key={p.id}
            type="button"
            data-testid={`preset-${p.id}`}
            title={p.description}
            onClick={() => onApply(p.filter)}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
              isActive
                ? "border-blue-300 bg-blue-50 text-blue-700 shadow-sm"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Sparkles className="h-3 w-3" />
            {p.name}
          </button>
        );
      })}
      {personalPresets.map(renderPersonal)}
      {teamPresets.length > 0 && (
        <span
          data-testid="presets-team-divider"
          className="ml-2 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600"
        >
          <UsersIcon className="mr-1 inline h-3 w-3" />
          Team
        </span>
      )}
      {teamPresets.map(renderTeam)}
      {loading && (
        <Loader2
          data-testid="presets-loading"
          className="h-3 w-3 animate-spin text-slate-400"
        />
      )}
      {naming ? (
        <span className="inline-flex flex-wrap items-center gap-2 rounded-full border border-blue-300 bg-blue-50 p-1 pl-3 text-[11px] text-blue-900">
          <input
            data-testid="preset-name-input"
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") {
                setNaming(false);
                setDraftName("");
                setDraftShared(false);
              }
            }}
            placeholder="Preset name"
            maxLength={40}
            disabled={saving}
            className="w-32 bg-transparent text-[11px] font-semibold uppercase tracking-[0.16em] outline-none placeholder:text-blue-400"
          />
          {isTeamOwner && (
            <label
              className="inline-flex cursor-pointer items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700"
              title="Make this preset available to every teammate on your account"
            >
              <input
                type="checkbox"
                data-testid="preset-share-team-checkbox"
                checked={draftShared}
                onChange={(e) => setDraftShared(e.target.checked)}
                disabled={saving}
                className="h-3 w-3 accent-violet-600"
              />
              <UsersIcon className="h-3 w-3" />
              Share with team
            </label>
          )}
          <button
            type="button"
            data-testid="preset-save-confirm"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            data-testid="preset-save-cancel"
            onClick={() => {
              setNaming(false);
              setDraftName("");
              setDraftShared(false);
            }}
            disabled={saving}
            className="rounded-full p-0.5 text-blue-400 hover:bg-blue-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          data-testid="preset-save-btn"
          onClick={() => setNaming(true)}
          disabled={isDefaultView(currentFilter)}
          title={
            isDefaultView(currentFilter)
              ? "Change a filter or sort to save a preset"
              : "Save current filter as preset"
          }
          className={`inline-flex items-center gap-1 rounded-full border border-dashed px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
            isDefaultView(currentFilter)
              ? "cursor-not-allowed border-slate-200 text-slate-400"
              : "border-slate-300 text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          }`}
        >
          <BookmarkPlus className="h-3 w-3" />
          Save view
        </button>
      )}
    </div>
  );
}
